import { cn } from '@/shared/utils/cn';
import type {
  CatalogCardCatalogPropsFlat,
  CatalogCardInteractionContext,
  CatalogCardMediaIndicator,
  CatalogCardPrimaryVariant,
} from '../../types';
import { catalogCardDefaultMediaIndicators } from '../../constants/mediaIndicators';
import {
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import type { PopupRef } from '@/shared/types/popup';
import { Picture } from '@/modules/Picture';
import { Badge } from '@/modules/Badge';
import { Icon } from '@/modules/Icon';
import { Button } from '@/modules/Button';
import { Popup } from '@/modules/Popup';
import { Tooltip, TruncatedWithTooltip } from '@/modules/Tooltip';
import {
  faBookmarkPlus,
  faCircleCheck,
  faEllipsis,
  faEye,
  faTrashCan,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { faBadgeCheck } from '@fortawesome/pro-duotone-svg-icons';
import { faLocationDot, faStar } from '@fortawesome/pro-solid-svg-icons';
import { OptionsMenuPanel, PrimaryMenuPanel } from './catalogCardMenuPanels';
import { stopCardClick } from './catalogCardMenuPanelUtils';
import {
  CatalogCatalogCardStackedHeaderTag,
  CatalogCatalogCardTagsRow,
} from './catalogCardTagsRow';
import { normalizeCatalogCardTags } from '../../utils/catalogCardTagUtils';
import { getCatalogCardRatingDisplayText } from '../../utils/catalogCardRatingDisplay';
import { isCatalogCardMediaIndicatorText } from '../../utils/catalogCardMediaIndicatorUtils';

const defaultPrimaryLabels = {
  add: 'Agregar',
  added: 'Agregado',
  remove: 'Quitar',
  deleted: 'Eliminado',
} as const;

const defaultCatalogOptionsTooltip = 'Más opciones';
const defaultCatalogRatingTooltip = 'Calificación del medio';

/**
 * CTA *Agregado* con hover (check → xmark, texto → *Quitar*).
 * Fila `inline-flex` + `mx-auto` para centrar el grupo en el botón; la caja de texto
 * mide `max(Agregado n/m, Quitar)` con grid invisible; las capas usan `grid place-items-center`
 * para que “Quitar” no quede alineado a la izquierda dentro de la caja ancha.
 */
function CatalogAddedHoverTriggerContent({
  labelText,
  showLeadingIcon = true,
}: {
  labelText: string;
  showLeadingIcon?: boolean;
}) {
  const textLayerInner =
    'ui-block ui-w-max ui-max-w-full ui-whitespace-nowrap ui-text-center ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-none';

  return (
    <span className="ui-mx-auto ui-inline-flex ui-max-w-full ui-items-center ui-justify-center ui-gap-8">
      {showLeadingIcon ? (
        <span className="ui-relative ui-flex ui-h-16 ui-w-16 ui-shrink-0 ui-items-center ui-justify-center">
          <Icon
            name={faCircleCheck}
            size="sm"
            color="current"
            className="ui-transition-opacity group-hover:ui-opacity-0"
            aria-hidden
          />
          <span
            className="ui-pointer-events-none ui-absolute ui-inset-0 ui-grid ui-place-items-center ui-opacity-0 ui-transition-opacity group-hover:ui-opacity-100"
            aria-hidden
          >
            <Icon name={faXmark} size="sm" color="current" />
          </span>
        </span>
      ) : null}
      <span className="ui-relative ui-inline-grid ui-shrink-0">
        <span className="ui-invisible ui-col-start-1 ui-row-start-1 ui-inline-grid ui-grid-cols-1">
          <span className="ui-col-start-1 ui-row-start-1 ui-whitespace-nowrap ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-none">
            {labelText}
          </span>
          <span className="ui-col-start-1 ui-row-start-1 ui-whitespace-nowrap ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-none">
            {defaultPrimaryLabels.remove}
          </span>
        </span>
        <span className="ui-absolute ui-inset-0 ui-grid ui-place-items-center ui-opacity-100 ui-transition-opacity group-hover:ui-opacity-0">
          <span className={textLayerInner}>{labelText}</span>
        </span>
        <span className="ui-absolute ui-inset-0 ui-grid ui-place-items-center ui-opacity-0 ui-transition-opacity group-hover:ui-opacity-100">
          <span className={textLayerInner}>{defaultPrimaryLabels.remove}</span>
        </span>
      </span>
    </span>
  );
}

/**
 * Variante catálogo: media, detalle y CTA cerrados por props.
 * Vertical: ~250px de ancho (Figma) salvo **`fullWidth`**. Horizontal: ancho fluido, miniatura a la izquierda a altura completa.
 * Stacked: cabecera (thumb **1/3** del ancho de la tarjeta en **16:9** + rating + primera tag), cuerpo, pie (tags + acciones). Con **`hideImage`**, cabecera solo rating + primera tag.
 * Compact: cabecera banda (thumb **1/3** del ancho en **16:9** + calificación + primera tag debajo + CTA ícono a la derecha; sin menú ⋯), cuerpo categoría/título/ubicación. Con **`hideImage`**, sin miniatura: misma cabecera en una sola fila.
 */
export const CatalogCatalogCard = forwardRef<HTMLDivElement, CatalogCardCatalogPropsFlat>(
  function CatalogCatalogCard(
    {
      className,
      selected = false,
      emphasized = false,
      subtleBorder = false,
      disabled = false,
      layout = 'vertical',
      fullWidth = false,
      hidePrimaryCta = false,
      to,
      href,
      cardLinkTarget = '_self',
      onClick,
      imageSrc,
      imageAlt = '',
      hideImage = false,
      showExclusiveBadge = false,
      indicators,
      showAvailabilityIndicators = false,
      categoryLabel,
      ratingValue,
      title,
      locationLabel,
      tags: tagsProp = [],
      primarySelectedTags: primarySelectedTagsControlled,
      defaultPrimarySelectedTags = [],
      onPrimaryTagsSelectionChange,
      primaryTagsSelectAllLabel,
      primaryTagsShowSelectAll = false,
      optionsPressed = false,
      hideOptionsMenu,
      primaryLinkHref,
      primaryLinkTo,
      primaryLinkLabel = 'Ver más',
      primaryLinkTarget = '_self',
      primaryVariant = 'add',
      primaryLabel,
      primaryIcon: primaryIconProp,
      onPrimaryClick,
      primaryDisabled = false,
      primaryBadge,
      onOptionsClick,
      optionsMenuItems,
      primaryMenuItems,
      primaryMenuFooter,
      primaryMenuMaxHeightClass,
      optionsTooltip,
      ratingTooltip,
      hideRating = false,
    },
    ref
  ) {
    /** If unset: hide ⋯ when `hideImage`; otherwise keep previous behavior (explicit props override). */
    const effectiveHideOptionsMenu = hideOptionsMenu ?? hideImage;

    const isHorizontal = layout === 'horizontal';
    const isStacked = layout === 'stacked';
    const isCompact = layout === 'compact';
    const useFluidCatalogWidth = isHorizontal || isStacked || isCompact || fullWidth;
    const optionsMenuPopupRef = useRef<PopupRef>(null);
    const primaryMenuPopupRef = useRef<PopupRef>(null);
    const [internalPrimarySelectedTags, setInternalPrimarySelectedTags] = useState<string[]>(() => [
      ...defaultPrimarySelectedTags,
    ]);

    const tagsContentSignature = normalizeCatalogCardTags(tagsProp)
      .map(
        (t) =>
          `${t.label}\u0000${t.color}\u0000${t.hideInTagRow ?? ''}\u0000${t.disabled ?? ''}\u0000${t.panelBadge?.label ?? ''}`
      )
      .join('\u0001');
    const resolvedTags = useMemo(
      () => normalizeCatalogCardTags(tagsProp),
      // tagsContentSignature evita recomputar cuando el padre pasa `tags` inline con el mismo contenido.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- tagsProp omitido a propósito (ver signature)
      [tagsContentSignature]
    );
    const tagRowTags = useMemo(
      () => resolvedTags.filter((t) => !t.hideInTagRow),
      [resolvedTags]
    );
    const tagLabels = resolvedTags.map((t) => t.label);
    const selectableTagLabels = useMemo(
      () => resolvedTags.filter((t) => !t.disabled).map((t) => t.label),
      [resolvedTags]
    );
    const tagsPrimaryCheckboxMode = Boolean(onPrimaryTagsSelectionChange && tagLabels.length > 0);
    const primarySelectedTags = primarySelectedTagsControlled ?? internalPrimarySelectedTags;
    const hasPrimaryTagSelection = primarySelectedTags.length > 0;

    const cardContext: CatalogCardInteractionContext = useMemo(
      () => ({ family: 'catalog', title, primaryVariant }),
      [title, primaryVariant]
    );

    useEffect(() => {
      if (primarySelectedTagsControlled !== undefined) return;
      setInternalPrimarySelectedTags((prev) =>
        prev.filter((t) => selectableTagLabels.includes(t))
      );
    }, [selectableTagLabels, primarySelectedTagsControlled]);

    const setPrimarySelectedTags = (next: string[]) => {
      const enabled = new Set(selectableTagLabels);
      const sanitized = next.filter((t) => enabled.has(t));
      if (primarySelectedTagsControlled === undefined) {
        setInternalPrimarySelectedTags(sanitized);
      }
      onPrimaryTagsSelectionChange?.(sanitized, cardContext);
    };

    const primaryCtaDisabled = disabled || primaryDisabled;
    const primaryButtonBadge = primaryBadge?.label?.trim()
      ? { show: true, color: 'danger' as const, theme: 'solid' as const, size: 'sm' as const, ...primaryBadge }
      : undefined;
    const primaryText =
      primaryLabel ?? defaultPrimaryLabels[primaryVariant as CatalogCardPrimaryVariant];
    /** Panel de tags (233:13284): sin selección → *Agregar* + bookmark + primary; con selección → *Agregado n/m* + check + secondary. */
    const variantPrimaryIcon =
      tagsPrimaryCheckboxMode && tagLabels.length > 0
        ? hasPrimaryTagSelection
          ? primaryVariant === 'add'
            ? faBookmarkPlus
            : primaryVariant === 'added'
              ? faCircleCheck
              : primaryVariant === 'deleted'
                ? faTrashCan
                : faXmark
          : faBookmarkPlus
        : primaryVariant === 'add'
          ? faBookmarkPlus
          : primaryVariant === 'added'
            ? faCircleCheck
            : primaryVariant === 'deleted'
              ? faTrashCan
              : faXmark;
    const resolvedVariantPrimaryIcon = primaryIconProp ?? variantPrimaryIcon;
    /** Figma 233:13284: con opciones en el panel, *Agregado n/m* solo cuando hay al menos una etiqueta elegida; si no, *Agregar*. */
    const primaryTriggerLabel =
      tagsPrimaryCheckboxMode && selectableTagLabels.length > 0
        ? hasPrimaryTagSelection
          ? `${primaryText} ${primarySelectedTags.length}/${selectableTagLabels.length}`
          : (primaryLabel ?? defaultPrimaryLabels.add)
        : primaryText;
    const resolvedPrimaryFooter =
      primaryMenuFooter ??
      (tagsPrimaryCheckboxMode ? { label: 'Cerrar', onClick: () => {} } : undefined);
    const hasOptionsMenu = Boolean(optionsMenuItems?.length);
    const primaryLinkDestination = primaryLinkTo ?? primaryLinkHref;
    const isPrimaryLink = Boolean(primaryLinkDestination);
    const hasPrimaryMenu =
      !isPrimaryLink && (Boolean(primaryMenuItems?.length) || tagsPrimaryCheckboxMode);

    /** Panel de tags + *Agregado n/m*: mismo hover que CTA *added* sin panel (Quitar + xmark). */
    const showAddedTagsPanelHoverRemove =
      tagsPrimaryCheckboxMode &&
      tagLabels.length > 0 &&
      hasPrimaryTagSelection &&
      primaryVariant === 'added';

    const primaryMenuPopupLabel = showAddedTagsPanelHoverRemove ? (
      <CatalogAddedHoverTriggerContent labelText={primaryTriggerLabel} />
    ) : (
      primaryTriggerLabel
    );

    const widthClass = useFluidCatalogWidth
      ? 'ui-w-full ui-min-w-0 ui-max-w-full'
      : 'ui-w-[250px] ui-min-w-[250px] ui-max-w-[250px]';

    const resolvedIndicators: CatalogCardMediaIndicator[] =
      indicators && indicators.length > 0
        ? indicators
        : showAvailabilityIndicators
          ? catalogCardDefaultMediaIndicators
          : [];

    const handleRootClick = (e: MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;
      e.stopPropagation();
      onClick(cardContext);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!onClick) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(cardContext);
      }
    };

    const cardDestination = (to ?? href ?? '').trim();
    const isCardLink = cardDestination.length > 0;
    /** Con `disabled`, no hay `<a>` ni navegación desde la zona principal. */
    const effectiveCardLink = Boolean(isCardLink && !disabled);
    /** Clic/teclado en la raíz: siempre que haya `onClick` y no haya enlace efectivo (incluye tarjeta bloqueada + `onClick`). */
    const rootCardClickActive = Boolean(onClick && !effectiveCardLink);

    const hasCategory = Boolean(categoryLabel?.trim());
    const trimmedRating = (ratingValue ?? '').trim();
    const ratingDisplayText = getCatalogCardRatingDisplayText(trimmedRating);
    /** Hay calificación explícita (texto no vacío). Sin calificación: estrella `disabled` y texto fijo `0.0` (no configurable). */
    const hasRatedValue = trimmedRating.length > 0;
    const showRatingBlock = !hideRating && (hasCategory || hasRatedValue);
    const showCategoryMetaRow = hasCategory || (!hideRating && hasRatedValue);
    const showLocationRow = Boolean(locationLabel?.trim());

    const resolvedOptionsTooltip =
      optionsTooltip === undefined ? defaultCatalogOptionsTooltip : optionsTooltip;
    const resolvedRatingTooltip =
      ratingTooltip === undefined ? defaultCatalogRatingTooltip : ratingTooltip;
    const optionsTriggerTooltip =
      resolvedOptionsTooltip.trim().length > 0 ? resolvedOptionsTooltip : undefined;
    const ratingBlockTooltip =
      resolvedRatingTooltip.trim().length > 0 ? resolvedRatingTooltip : undefined;

    /**
     * CTA principal: ancho completo (pie vertical) o botón solo ícono (layout horizontal).
     */
    const getPrimaryCtaNode = (placement: 'footer' | 'horizontalIcon') => {
      const iconOnly = placement === 'horizontalIcon';
      const triggerTooltip = iconOnly
        ? {
            label:
              typeof primaryTriggerLabel === 'string' ? primaryTriggerLabel : String(primaryText),
            position: 'top' as const,
          }
        : undefined;

      if (isPrimaryLink) {
        return (
          <Button
            {...(disabled ? {} : { link: primaryLinkDestination, target: primaryLinkTarget })}
            color="primary"
            theme="solid"
            size="sm"
            pill={iconOnly ? 'pill' : 'sm'}
            square={iconOnly}
            {...(iconOnly
              ? {
                  iconProps: {
                    icon: primaryIconProp ?? faEye,
                    show: true,
                    color: 'current',
                    size: 'sm',
                  },
                }
              : { leftSlot: primaryIconProp ?? faEye })}
            fullWidth={!iconOnly}
            textAlign="center"
            className={cn(!iconOnly && 'ui-justify-center')}
            disabled={primaryCtaDisabled}
            aria-label={primaryLinkLabel}
            badge={primaryButtonBadge}
            tooltip={iconOnly ? { label: primaryLinkLabel, position: 'top' as const } : undefined}
          >
            {iconOnly ? null : primaryLinkLabel}
          </Button>
        );
      }
      if (hasPrimaryMenu) {
        return (
          <Popup
            ref={primaryMenuPopupRef}
            position={tagsPrimaryCheckboxMode ? 'top-right' : 'bottom-left'}
            popoverArrow={false}
            closeOnClick={tagsPrimaryCheckboxMode ? false : undefined}
            color={
              primaryVariant === 'deleted'
                ? 'destructive'
                : tagsPrimaryCheckboxMode && hasPrimaryTagSelection
                  ? 'secondary'
                  : 'primary'
            }
            size="sm"
            pill={iconOnly ? 'pill' : 'sm'}
            square={iconOnly}
            absolute
            theme="solid"
            offset={tagsPrimaryCheckboxMode ? 8 : 6}
            fullWidth={!iconOnly}
            containerClassName={iconOnly ? undefined : 'ui-w-full'}
            label={iconOnly ? undefined : primaryMenuPopupLabel}
            {...(iconOnly
              ? {
                  iconProps: {
                    icon: resolvedVariantPrimaryIcon,
                    show: true,
                    color: 'current',
                    size: 'sm',
                  },
                }
              : {
                  leftSlot: showAddedTagsPanelHoverRemove ? undefined : resolvedVariantPrimaryIcon,
                })}
            textAlign="center"
            disabled={primaryCtaDisabled}
            tooltip={iconOnly ? triggerTooltip : undefined}
            badge={primaryButtonBadge}
            buttonClassName={cn(
              'ui-min-w-0 ui-justify-center ui-items-center',
              !iconOnly && showAddedTagsPanelHoverRemove && 'ui-group'
            )}
          >
            <PrimaryMenuPanel
              items={primaryMenuItems ?? []}
              tags={tagsPrimaryCheckboxMode ? resolvedTags : undefined}
              selectedTags={primarySelectedTags}
              onTagsChange={tagsPrimaryCheckboxMode ? setPrimarySelectedTags : undefined}
              selectAllLabel={primaryTagsSelectAllLabel}
              tagsShowSelectAll={primaryTagsShowSelectAll}
              footer={resolvedPrimaryFooter}
              maxHeightClass={primaryMenuMaxHeightClass}
              onDismiss={() => primaryMenuPopupRef.current?.close()}
            />
          </Popup>
        );
      }
      if (primaryVariant === 'added') {
        return (
          <Button
            color="secondary"
            theme="solid"
            size="sm"
            pill={iconOnly ? 'pill' : 'sm'}
            square={iconOnly}
            {...(iconOnly
              ? {
                  iconProps: {
                    icon: faCircleCheck,
                    show: true,
                    color: 'current',
                    size: 'sm',
                  },
                }
              : { leftSlot: primaryIconProp })}
            fullWidth={!iconOnly}
            textAlign="center"
            className={cn('ui-group', !iconOnly && 'ui-justify-center')}
            disabled={primaryCtaDisabled}
            aria-label={primaryText}
            badge={primaryButtonBadge}
            tooltip={iconOnly ? { label: primaryText, position: 'top' as const } : undefined}
            onClick={(e) => {
              stopCardClick(e);
              onPrimaryClick?.(cardContext);
            }}
          >
            {iconOnly ? null : <CatalogAddedHoverTriggerContent labelText={primaryText} />}
          </Button>
        );
      }
      return (
        <Button
          color={
            primaryVariant === 'deleted'
              ? 'destructive'
              : primaryVariant === 'add'
                ? 'primary'
                : 'secondary'
          }
          theme="solid"
          size="sm"
          pill={iconOnly ? 'pill' : 'sm'}
          square={iconOnly}
          {...(iconOnly
            ? {
                iconProps: {
                  icon: resolvedVariantPrimaryIcon,
                  show: true,
                  color: 'current',
                  size: 'sm',
                },
              }
            : { leftSlot: resolvedVariantPrimaryIcon })}
          fullWidth={!iconOnly}
          textAlign="center"
          className={cn(!iconOnly && 'ui-justify-center')}
          disabled={primaryCtaDisabled}
          aria-label={primaryText}
          badge={primaryButtonBadge}
          tooltip={iconOnly ? { label: primaryText, position: 'top' as const } : undefined}
          onClick={(e) => {
            stopCardClick(e);
            onPrimaryClick?.(cardContext);
          }}
        >
          {iconOnly ? null : primaryText}
        </Button>
      );
    };

    const primaryCta =
      !hidePrimaryCta && !isHorizontal && !isStacked && !isCompact
        ? getPrimaryCtaNode('footer')
        : null;
    const primaryCtaHorizontalIcon =
      !hidePrimaryCta && (isHorizontal || isStacked || isCompact)
        ? getPrimaryCtaNode('horizontalIcon')
        : null;

    const optionsTrigger = effectiveHideOptionsMenu ? null : hasOptionsMenu ? (
      <Popup
        ref={optionsMenuPopupRef}
        position="top-left"
        color="secondary"
        size="xs"
        popoverArrow={false}
        pill="pill"
        rounded
        theme="solid"
        icon={faEllipsis}
        tooltip={
          optionsTriggerTooltip
            ? { label: optionsTriggerTooltip, position: 'top' as const }
            : undefined
        }
        buttonClassName={cn(optionsPressed ? 'ui-bg-fill-secondary-hover' : undefined)}
      >
        <OptionsMenuPanel
          items={optionsMenuItems!}
          onDismiss={() => optionsMenuPopupRef.current?.close()}
        />
      </Popup>
    ) : (
      <Button
        color="secondary"
        size="xs"
        pill="pill"
        theme="solid"
        icon={faEllipsis}
        tooltip={
          optionsTriggerTooltip
            ? { label: optionsTriggerTooltip, position: 'top' as const }
            : undefined
        }
        className={cn(optionsPressed ? 'ui-bg-fill-secondary-hover' : undefined)}
        aria-label="Opciones"
        onClick={(e) => {
          stopCardClick(e);
          onOptionsClick?.(cardContext);
        }}
      />
    );

    const showCatalogFooter =
      !isHorizontal && !isStacked && !isCompact && (Boolean(optionsTrigger) || Boolean(primaryCta));

    const rootClassName = cn(
      'ui-imj',
      !disabled && 'ui-group',
      'ui-relative ui-flex ui-flex-col ui-gap-16 ui-rounded-surface-card ui-border-1',
      subtleBorder ? 'ui-border-subtle ui-bg-fill' : 'ui-border-default ui-bg-fill',
      widthClass,
      emphasized ? 'ui-bg-surface-brand-hover ui-shadow-03' : '',
      !disabled &&
        'ui-transition-all ui-duration-200 hover:ui-bg-surface-brand-hover hover:ui-shadow-01-03',
      (!disabled && isCardLink) || onClick ? 'ui-cursor-pointer' : '',
      disabled && !onClick ? 'ui-cursor-default' : '',
      className
    );

    const catalogMainInnerClass = cn(
      'ui-min-w-0 ui-w-full',
      isHorizontal && !hideImage
        ? 'ui-grid ui-grid-cols-[minmax(120px,4rem)_minmax(0,1fr)] ui-items-stretch ui-gap-12'
        : 'ui-flex ui-flex-col ui-gap-16'
    );

    const catalogLinkMainClass = cn(
      'ui-block ui-min-w-0 ui-w-full ui-text-inherit ui-no-underline',
      'focus-visible:ui-outline focus-visible:ui-outline-2 focus-visible:ui-outline-offset-2 focus-visible:ui-outline-brand'
    );

    const catalogRatingBlockInner = (
      <div className="ui-flex ui-min-w-0 ui-shrink-0 ui-items-center ui-gap-4">
        <Icon name={faStar} size="xs" color={hasRatedValue ? 'yellow' : 'disabled'} />
        <TruncatedWithTooltip
          title={ratingDisplayText}
          position="top"
          maxVisibleChars={5}
          className={cn(
            'ui-text-[10px] ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-[2px]',
            emphasized ? 'ui-text-brand' : 'ui-text-tertiary'
          )}
        >
          {ratingDisplayText}
        </TruncatedWithTooltip>
      </div>
    );

    const catalogRatingBlock = showRatingBlock ? (
      ratingBlockTooltip ? (
        <Tooltip label={ratingBlockTooltip} position="top" preserveLayout>
          {catalogRatingBlockInner}
        </Tooltip>
      ) : (
        catalogRatingBlockInner
      )
    ) : null;

    const renderCatalogMediaOverlays = (placement: 'vertical' | 'horizontal' | 'thumb') => {
      const exclusiveClassName = cn(
        'ui-pointer-events-none ui-absolute ui-z-10',
        placement === 'thumb'
          ? 'ui-top-4 ui-left-4'
          : placement === 'horizontal'
            ? 'ui-top-8 ui-right-8'
            : 'ui-top-8 ui-left-8'
      );
      const indicatorsClassName = cn(
        'ui-pointer-events-none ui-absolute ui-z-10 ui-flex ui-items-center ui-gap-4',
        placement === 'thumb'
          ? 'ui-top-4 ui-right-4'
          : placement === 'horizontal'
            ? 'ui-top-8 ui-left-8'
            : 'ui-top-8 ui-right-8'
      );
      return (
        <>
          {showExclusiveBadge ? (
            <div className={exclusiveClassName}>
              <Icon
                name={faBadgeCheck}
                size="sm"
                padding="xxs"
                colorDuotonePrimary="green"
                colorDuotoneSecondary="success"
                opacityDuotonePrimary={0.4}
                opacityDuotoneSecondary={1}
                background
                backgroundColor="white"
              />
            </div>
          ) : null}
          {resolvedIndicators.length > 0 ? (
            <div className={indicatorsClassName}>
              {resolvedIndicators.map((indicator, index) => {
                if (isCatalogCardMediaIndicatorText(indicator)) {
                  const {
                    id,
                    label,
                    tooltip,
                    tooltipProps,
                    className: indicatorClassName,
                    color = 'gray',
                    theme = 'soft',
                    size = 'lg',
                    stroke = false,
                    disabled: indicatorDisabled = false,
                  } = indicator;
                  const key = id ?? `media-indicator-text-${index}`;
                  const textWrap = (
                    <span className="ui-pointer-events-auto ui-inline-flex ui-shrink-0">
                      <Badge
                        label={label}
                        color={color}
                        theme={theme}
                        size={size}
                        stroke={stroke}
                        disabled={indicatorDisabled}
                        truncateLabel
                        hover={false}
                        className={cn(
                          'ui-rounded-control-pill',
                          'ui-max-w-[min(100%,140px)]',
                          color === 'gray' && '!ui-text-primary [&_span]:!ui-text-primary',
                          indicatorClassName
                        )}
                      />
                    </span>
                  );
                  if (tooltip !== null && tooltip !== undefined && String(tooltip).length > 0) {
                    return (
                      <Tooltip
                        key={key}
                        label={tooltip}
                        position="top"
                        preserveLayout
                        {...tooltipProps}
                      >
                        {textWrap}
                      </Tooltip>
                    );
                  }
                  return <Fragment key={key}>{textWrap}</Fragment>;
                }
                const { id, tooltip, tooltipProps, ...iconProps } = indicator;
                const key = id ?? `media-indicator-${index}`;
                const iconWrap = (
                  <span className="ui-pointer-events-auto ui-inline-flex ui-shrink-0">
                    <Icon {...iconProps} size="xs" padding="xs" />
                  </span>
                );
                if (tooltip !== null && tooltip !== undefined && String(tooltip).length > 0) {
                  return (
                    <Tooltip
                      key={key}
                      label={tooltip}
                      position="top"
                      preserveLayout
                      {...tooltipProps}
                    >
                      {iconWrap}
                    </Tooltip>
                  );
                }
                return <Fragment key={key}>{iconWrap}</Fragment>;
              })}
            </div>
          ) : null}
        </>
      );
    };

    const mediaShellClassName = cn(
      'ui-relative ui-overflow-hidden',
      isHorizontal
        ? 'ui-h-full ui-min-h-[120px] ui-w-full ui-max-w-[200px] ui-rounded-l-[var(--ui-radius-surface-card)]'
        : 'ui-h-[141px] ui-w-full ui-shrink-0 ui-rounded-t-[var(--ui-radius-surface-card)]'
    );

    const catalogContentColumnClassName = cn(
      'ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-4',
      isHorizontal && !hideImage && 'ui-min-h-0 ui-py-12 ui-pr-16',
      !(isHorizontal && !hideImage) && 'ui-px-16',
      hideImage && 'ui-pt-16',
      // No footer row: preserve bottom inset that `catalogFooter` would add (vertical layout).
      !showCatalogFooter && !isHorizontal && !isStacked && !isCompact && 'ui-pb-16',
      isHorizontal && hideImage && 'ui-pb-12'
    );

    /** Enlace solo del área de textos (horizontal / stacked / compact); evita anidar botones dentro del `<a>`. */
    const catalogLinkTextAreaClassName = cn(
      'ui-flex ui-min-w-0 ui-w-full ui-flex-1 ui-flex-col ui-gap-4 ui-text-inherit ui-no-underline',
      'focus-visible:ui-outline focus-visible:ui-outline-2 focus-visible:ui-outline-offset-2 focus-visible:ui-outline-brand'
    );

    const catalogBodyCategoryTitleLocationInner = (
      <>
        {hasCategory ? (
          <div className="ui-w-full ui-min-w-0 ui-overflow-hidden">
            <TruncatedWithTooltip
              title={categoryLabel}
              position="top"
              className={cn(
                'ui-text-[10px] ui-font-body-xs-regular ui-uppercase ui-leading-body-xs-regular ui-tracking-[2px]',
                emphasized
                  ? 'ui-text-brand'
                  : disabled
                    ? 'ui-text-tertiary'
                    : 'ui-text-tertiary group-hover:ui-text-brand'
              )}
            >
              {categoryLabel}
            </TruncatedWithTooltip>
          </div>
        ) : null}
        <div className="ui-w-full ui-min-w-0">
          <TruncatedWithTooltip
            title={title}
            position="top"
            className={cn(
              'ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm',
              emphasized
                ? 'ui-text-brand'
                : disabled
                  ? 'ui-text-primary'
                  : 'ui-text-primary group-hover:ui-text-brand'
            )}
          >
            {title}
          </TruncatedWithTooltip>
        </div>
        {showLocationRow ? (
          <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-4">
            <span
              className={cn(
                'ui-inline-flex ui-shrink-0',
                emphasized
                  ? 'ui-text-brand'
                  : disabled
                    ? 'ui-text-secondary'
                    : 'ui-text-secondary group-hover:ui-text-brand'
              )}
            >
              <Icon name={faLocationDot} size="xs" color="current" />
            </span>
            <div className="ui-min-w-0 ui-flex-1 ui-overflow-hidden">
              <TruncatedWithTooltip
                title={locationLabel}
                position="top"
                className={cn(
                  'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
                  emphasized
                    ? 'ui-text-brand'
                    : disabled
                      ? 'ui-text-secondary'
                      : 'ui-text-secondary group-hover:ui-text-brand'
                )}
              >
                {locationLabel}
              </TruncatedWithTooltip>
            </div>
          </div>
        ) : null}
      </>
    );

    const catalogCatalogLinkedBodyCategoryTitleLocation = effectiveCardLink ? (
      <a
        href={cardDestination}
        target={cardLinkTarget}
        rel={cardLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
        className={catalogLinkTextAreaClassName}
        aria-label={title}
        onClick={() => {
          onClick?.(cardContext);
        }}
      >
        {catalogBodyCategoryTitleLocationInner}
      </a>
    ) : (
      <div className="ui-flex ui-min-w-0 ui-flex-col ui-gap-4">
        {catalogBodyCategoryTitleLocationInner}
      </div>
    );

    /** Bloque de textos (categoría + rating, título, ubicación, tags) compartido vertical / horizontal. */
    const catalogCatalogTextStack = (
      <>
        {showCategoryMetaRow ? (
          <div
            className={cn(
              'ui-flex ui-w-full ui-items-center ui-gap-8',
              hasCategory ? 'ui-justify-between' : 'ui-justify-end'
            )}
          >
            {categoryLabel?.trim() ? (
              <div className="ui-min-w-0 ui-flex-1 ui-overflow-hidden">
                <TruncatedWithTooltip
                  title={categoryLabel}
                  position="top"
                  className={cn(
                    'ui-text-[10px] ui-font-body-xs-regular ui-uppercase ui-leading-body-xs-regular ui-tracking-[2px]',
                    emphasized
                      ? 'ui-text-brand'
                      : disabled
                        ? 'ui-text-tertiary'
                        : 'ui-text-tertiary group-hover:ui-text-brand'
                  )}
                >
                  {categoryLabel}
                </TruncatedWithTooltip>
              </div>
            ) : null}
            {catalogRatingBlock}
          </div>
        ) : null}
        <div className="ui-w-full ui-min-w-0">
          <TruncatedWithTooltip
            title={title}
            position="top"
            className={cn(
              'ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm',
              emphasized
                ? 'ui-text-brand'
                : disabled
                  ? 'ui-text-primary'
                  : 'ui-text-primary group-hover:ui-text-brand'
            )}
          >
            {title}
          </TruncatedWithTooltip>
        </div>
        {showLocationRow ? (
          <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-4">
            <span
              className={cn(
                'ui-inline-flex ui-shrink-0',
                emphasized
                  ? 'ui-text-brand'
                  : disabled
                    ? 'ui-text-secondary'
                    : 'ui-text-secondary group-hover:ui-text-brand'
              )}
            >
              <Icon name={faLocationDot} size="xs" color="current" />
            </span>
            <div className="ui-min-w-0 ui-flex-1 ui-overflow-hidden">
              <TruncatedWithTooltip
                title={locationLabel}
                position="top"
                className={cn(
                  'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
                  emphasized
                    ? 'ui-text-brand'
                    : disabled
                      ? 'ui-text-secondary'
                      : 'ui-text-secondary group-hover:ui-text-brand'
                )}
              >
                {locationLabel}
              </TruncatedWithTooltip>
            </div>
          </div>
        ) : null}
        {tagRowTags.length > 0 ? (
          <CatalogCatalogCardTagsRow
            tags={tagRowTags}
            emphasized={emphasized}
            disabled={disabled}
          />
        ) : null}
      </>
    );

    const catalogHorizontalActionRow = (
      <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-shrink-0 ui-flex-row ui-flex-nowrap ui-items-center ui-justify-end ui-gap-12 ui-pt-8">
        {optionsTrigger ? (
          <div className="ui-flex ui-shrink-0 ui-items-center">{optionsTrigger}</div>
        ) : null}
        <div className="ui-flex ui-shrink-0 ui-items-center">{primaryCtaHorizontalIcon}</div>
      </div>
    );

    const catalogVerticalAndHorizontalMain = (
      <div className={catalogMainInnerClass}>
        {!hideImage ? (
          <div className={mediaShellClassName}>
            <div className="ui-absolute ui-inset-0">
              {/* Sin URL `Picture` aplica su recurso por defecto (placeholder) cuando el bloque media está visible. */}
              <Picture
                src={imageSrc ?? ''}
                alt={imageAlt}
                fillParent
                size="auto"
                radius="none"
                shadow
                border={false}
                disabled={disabled}
                className="ui-absolute ui-inset-0 ui-select-none"
              />
            </div>
            {renderCatalogMediaOverlays(isHorizontal ? 'horizontal' : 'vertical')}
          </div>
        ) : null}

        <div className={catalogContentColumnClassName}>
          {isHorizontal ? (
            <>
              {effectiveCardLink ? (
                <a
                  href={cardDestination}
                  target={cardLinkTarget}
                  rel={cardLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  className={catalogLinkTextAreaClassName}
                  aria-label={title}
                  onClick={() => {
                    onClick?.(cardContext);
                  }}
                >
                  {catalogCatalogTextStack}
                </a>
              ) : (
                <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-4">
                  {catalogCatalogTextStack}
                </div>
              )}
              {catalogHorizontalActionRow}
            </>
          ) : (
            catalogCatalogTextStack
          )}
        </div>
      </div>
    );

    const firstStackedTag = tagRowTags[0];
    const catalogStackedMain = (
      <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-col ui-gap-16">
        {hideImage ? (
          <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-justify-between ui-gap-12 ui-px-16 ui-pt-16">
            <div className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-justify-center">
              {catalogRatingBlock}
            </div>
            <div className="ui-flex ui-min-w-0 ui-shrink-0 ui-items-center ui-justify-end">
              {firstStackedTag ? (
                <CatalogCatalogCardStackedHeaderTag
                  emphasized={emphasized}
                  disabled={disabled}
                  tag={firstStackedTag}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="ui-grid ui-w-full ui-min-w-0 ui-grid-cols-[minmax(0,1fr)_minmax(0,2fr)] ui-items-center ui-gap-12 ui-px-16 ui-pt-16">
            <div className="ui-relative ui-aspect-video ui-w-full ui-min-w-0 ui-overflow-hidden ui-rounded-image-sm">
              <Picture
                src={imageSrc ?? ''}
                alt={imageAlt}
                fillParent
                size="auto"
                radius="none"
                shadow
                border={false}
                disabled={disabled}
                className="ui-absolute ui-inset-0 ui-select-none"
              />
              {renderCatalogMediaOverlays('thumb')}
            </div>
            <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-row ui-items-center ui-gap-12">
              <div className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-justify-center">
                {catalogRatingBlock}
              </div>
              <div className="ui-flex ui-min-w-0 ui-shrink-0 ui-items-center ui-justify-end">
                {firstStackedTag ? (
                  <CatalogCatalogCardStackedHeaderTag
                    emphasized={emphasized}
                    disabled={disabled}
                    tag={firstStackedTag}
                  />
                ) : null}
              </div>
            </div>
          </div>
        )}
        <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-4 ui-px-16">
          {catalogCatalogLinkedBodyCategoryTitleLocation}
        </div>
        <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-row ui-flex-nowrap ui-items-center ui-justify-between ui-gap-12 ui-px-16 ui-pb-16">
          <div className="ui-min-w-0 ui-flex-1">
            <CatalogCatalogCardTagsRow
              className="!ui-pt-0"
              disabled={disabled}
              emphasized={emphasized}
              tags={tagRowTags}
            />
          </div>
          <div className="ui-flex ui-shrink-0 ui-items-center ui-gap-12">
            {optionsTrigger ? (
              <div className="ui-flex ui-shrink-0 ui-items-center">{optionsTrigger}</div>
            ) : null}
            <div className="ui-flex ui-shrink-0 ui-items-center">{primaryCtaHorizontalIcon}</div>
          </div>
        </div>
      </div>
    );

    const catalogCompactMain = (
      <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-col ui-gap-16">
        {hideImage ? (
          <div className="ui-flex ui-w-full ui-min-w-0 ui-items-start ui-justify-between ui-gap-12 ui-px-16 ui-pt-16">
            <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-items-start ui-gap-4">
              {catalogRatingBlock}
              {firstStackedTag ? (
                <CatalogCatalogCardStackedHeaderTag
                  emphasized={emphasized}
                  disabled={disabled}
                  tag={firstStackedTag}
                />
              ) : null}
            </div>
            <div className="ui-flex ui-shrink-0 ui-flex-col ui-items-end ui-gap-8 ui-self-start">
              <div className="ui-flex ui-shrink-0 ui-items-center">{primaryCtaHorizontalIcon}</div>
            </div>
          </div>
        ) : (
          <div className="ui-grid ui-w-full ui-min-w-0 ui-grid-cols-[minmax(0,1fr)_minmax(0,2fr)] ui-items-start ui-gap-12 ui-px-16 ui-pt-16">
            <div className="ui-relative ui-aspect-video ui-w-full ui-min-w-0 ui-overflow-hidden ui-rounded-image-sm">
              <Picture
                src={imageSrc ?? ''}
                alt={imageAlt}
                fillParent
                size="auto"
                radius="none"
                shadow
                border={false}
                disabled={disabled}
                className="ui-absolute ui-inset-0 ui-select-none"
              />
              {renderCatalogMediaOverlays('thumb')}
            </div>
            <div className="ui-flex ui-h-full ui-w-full ui-min-w-0 ui-flex-row ui-items-center ui-justify-between ui-gap-12">
              <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-items-start ui-gap-4">
                {catalogRatingBlock}
                {firstStackedTag ? (
                  <CatalogCatalogCardStackedHeaderTag
                    emphasized={emphasized}
                    disabled={disabled}
                    tag={firstStackedTag}
                  />
                ) : null}
              </div>
              <div className="ui-flex ui-shrink-0 ui-flex-col ui-items-end ui-gap-8 ui-self-start">
                <div className="ui-flex ui-shrink-0 ui-items-center">
                  {primaryCtaHorizontalIcon}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="ui-flex ui-min-w-0 ui-flex-col ui-gap-4 ui-px-16 ui-pb-16">
          {catalogCatalogLinkedBodyCategoryTitleLocation}
          {tagRowTags.length > 1 ? (
            <CatalogCatalogCardTagsRow
              className="!ui-pt-0"
              disabled={disabled}
              emphasized={emphasized}
              tags={tagRowTags.slice(1)}
            />
          ) : null}
        </div>
      </div>
    );

    const catalogMainBlocks = isStacked
      ? catalogStackedMain
      : isCompact
        ? catalogCompactMain
        : catalogVerticalAndHorizontalMain;

    const catalogFooter = showCatalogFooter ? (
      <div className="ui-flex ui-w-full ui-min-w-0 ui-items-center ui-gap-12 ui-px-16 ui-pb-16">
        {optionsTrigger}
        {!isHorizontal && primaryCta ? (
          <div className="ui-flex ui-w-full ui-min-w-0 ui-flex-1">{primaryCta}</div>
        ) : null}
      </div>
    ) : null;

    return (
      <div
        ref={ref}
        role={rootCardClickActive ? 'button' : undefined}
        tabIndex={rootCardClickActive ? 0 : undefined}
        aria-disabled={disabled && !onClick ? true : undefined}
        data-selected={selected ? true : undefined}
        data-disabled={disabled ? true : undefined}
        className={rootClassName}
        onClick={rootCardClickActive ? handleRootClick : undefined}
        onKeyDown={rootCardClickActive ? handleKeyDown : undefined}
      >
        {effectiveCardLink && !isHorizontal && !isStacked && !isCompact ? (
          <a
            href={cardDestination}
            target={cardLinkTarget}
            rel={cardLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
            className={catalogLinkMainClass}
            aria-label={title}
            onClick={() => {
              onClick?.(cardContext);
            }}
          >
            {catalogMainBlocks}
          </a>
        ) : (
          catalogMainBlocks
        )}
        {catalogFooter}
      </div>
    );
  }
);

CatalogCatalogCard.displayName = 'CatalogCatalogCard';
